import { BaseFilterComponent } from './types';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { Filter } from '../../types';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { Info } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { MultiCheckboxFilterOption } from '../options/types';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useIntl } from 'react-intl';
import * as React from 'react';

const useStyles = makeStyles(theme => ({
  infoIcon: {
    color: theme.palette.primary.main,
    margin: theme.spacing(0, 0, 0.75, 0.5),
  },
}));

interface MultiSelectCheckboxProps
  extends MultiCheckboxFilterOption,
    BaseFilterComponent {}

export const MultiSelectCheckbox: React.FC<MultiSelectCheckboxProps> = ({
  injectedProps: { checkboxOptions, searchKey },
  onFilterAdd,
  onFilterRemove,
  origin,
  selectedFilterOptions,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const [checkedState, setCheckedState] = React.useState<
    Record<string, boolean>
  >({});

  React.useEffect(() => {
    if (selectedFilterOptions?.length === 0) {
      setCheckedState({});
      return;
    }

    // We have no filters matching this search key,
    // so no need to evaluate
    const selectedFilterOptionsWithThisSearchKey =
      selectedFilterOptions?.filter(filter => filter.searchKey === searchKey);

    if (selectedFilterOptionsWithThisSearchKey?.length === 0) {
      return;
    }

    const allCheckboxOptionSearchValues = checkboxOptions?.map(
      checkboxOption => checkboxOption.searchValue,
    );

    // We check if our selected filter options for the provided search key
    // match up with any of our available checkbox options.  If so,
    // that means the checkbox should be checked; otherwise, it is unchecked.
    const intersection = selectedFilterOptionsWithThisSearchKey
      ?.map(filterOption =>
        allCheckboxOptionSearchValues?.find(
          checkboxOption => checkboxOption === filterOption.searchValue,
        ),
      )
      .filter(Boolean);

    const checkboxesState = {};

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    for (const searchKey of intersection) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      checkboxesState[searchKey] = true;
    }

    setCheckedState(checkboxesState);
  }, [checkboxOptions, searchKey, selectedFilterOptions]);

  const makeChangeHandler = (option: any) => (e: any) => {
    const value = e.target.checked;

    setCheckedState({
      ...checkedState,
      [option.searchValue]: e.target.checked,
    });

    const clonedOption: Filter = {
      friendlyName: formatMessage(option.intlLabel),
      id: option.id,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      origin,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchKey,
      searchValue: option.searchValue,
    };

    if (onFilterAdd && value) {
      onFilterAdd(clonedOption);
    }

    if (onFilterRemove && !value) {
      onFilterRemove(clonedOption);
    }
  };

  return (
    <FormBox spacing={1}>
      {checkboxOptions?.map(option => {
        return (
          <Box key={option.id}>
            <Checkbox
              label={formatMessage(option.intlLabel)}
              checked={Boolean(checkedState[option.searchValue])}
              onChange={makeChangeHandler(option)}
            />
            {option?.informationText && (
              <Tooltip title={formatMessage(option.informationText)}>
                <span>
                  <Info className={classes.infoIcon} height={18} width={18} />
                </span>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </FormBox>
  );
};
